/**
 * WHERE "INVESTED" MONEY GOES, in the card's words (owner, 23 Oct 2026: "invest what is left — invest in what
 * exactly?", then: not "shares", an asset class of its own and growth, but a portfolio of managed funds at the
 * client's risk profile). A plan's invested money is priced on the client's answer to "how would money that is
 * freed up be invested?" (newMoneyProfileOf); with no answer the cautious Conservative mix is assumed, and the
 * words say so.
 *
 * The profiles are held as returns only (investmentProfiles.ts), not as asset splits, so each mix is described
 * in words, never in percentages the figures do not model.
 */
import { INVESTMENT_PROFILES, newMoneyProfileOf, hasAnsweredNewMoneyProfile, type InvestmentProfileId } from '../../../shared/src/calculation/investmentProfiles.ts';

type FileLike = Parameters<typeof newMoneyProfileOf>[0];

/** What each mix holds, in plain words. */
export const PROFILE_PLAIN_WORDS: Record<InvestmentProfileId, string> = {
    conservative: 'mostly cash and fixed interest, with a smaller part in shares and property, for steadier returns',
    moderately_conservative: 'more in cash and fixed interest than in shares and property',
    balanced: 'shares and property, and cash and fixed interest, in roughly even measure',
    growth: 'mostly shares and property, with some cash and fixed interest',
    high_growth: 'shares and property only, for the most growth and the biggest ups and downs',
};

/** The card's marker: the phrase investedWhereWords always contains, read by plansInvest. */
const MARKER = 'portfolio of managed funds';

export function investedWhereWords(factFind: FileLike): string {
    const mix = INVESTMENT_PROFILES[newMoneyProfileOf(factFind)].label.toLowerCase();
    return `in a ${mix} ${MARKER}${hasAnsweredNewMoneyProfile(factFind) ? '' : ' (assumed, as no investment choice was given)'}`;
}

/** Whether a plan's card sentence says money is invested (so its Explain this says how it would be held). */
export function plansInvest(cardSentence: string | null | undefined): boolean {
    return String(cardSentence || '').includes(MARKER);
}

/** The Explain this note: what the mix is, and the usual ways such a portfolio is held. */
export function howItIsInvestedWords(factFind: FileLike): string[] {
    const id = newMoneyProfileOf(factFind);
    const mix = INVESTMENT_PROFILES[id].label.toLowerCase();
    return [
        `The plan assumes a ${mix} portfolio: ${PROFILE_PLAIN_WORDS[id]}.${hasAnsweredNewMoneyProfile(factFind) ? '' : ' No investment choice is on the file, so the cautious mix is assumed; choosing one in step 3 changes the figures.'}`,
        'It is usually held as a diversified managed fund or a managed portfolio, set up through a financial adviser or an investment platform, including online (digital) portfolios. Fees differ between providers, and returns go up and down: the figures use the mix’s long-run returns, which are not guaranteed.',
    ];
}
